import React, { useRef, useState } from 'react';
import { StyledOffer } from './Offer.style';
import BaseButton from '@/Components/atoms/BaseComponents/BaseButton';
import { useTranslation } from 'next-i18next';
import { IChangeOfferParams, IChatUserDto, IMakeOfferDto, IOfferDto, OfferStatusEnum } from '@/Models/Chat';
import OfferSkeleton from './OfferSkeleton';
import { CURRENCY } from '@/Models/Product/IProduct';
import numberService from '@/Services/NumberService';
import { useUserData } from '@/Hooks/useUserData';
import { IconButton, Input, Stack, Typography } from '@mui/material';
import CommonSvg, { svgNames } from '@/Components/atoms/CommonSvg';
import { BiCheckCircle } from 'react-icons/bi';
import { LiaFunnelDollarSolid } from 'react-icons/lia';
import { FiCheck } from 'react-icons/fi';
import OfferApi from '@/Services/Axios/Controllers/OfferApi';
import BaseInput from '@/Components/atoms/BaseComponents/BaseInput';
import { AxiosResponse } from 'axios';
import ChatApi from '@/Services/Axios/Controllers/ChatController';
import { logger } from '@/Services/Logger';

type Props = {
  offerData: IOfferDto;
  chatData: IChatUserDto;
  loading: boolean;
  reFetchMessages: () => void;

  makeOffer: (productId: number, body: IMakeOfferDto) => Promise<AxiosResponse<IOfferDto, any>>;
  changeOfferStatus: (params: IChangeOfferParams) => Promise<AxiosResponse<IOfferDto, any>>;
}

const Offer = ({ offerData, chatData, loading, reFetchMessages, changeOfferStatus, makeOffer }: Props) => {
  const { t } = useTranslation();
  const userData = useUserData()!;
  const baseInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [loadingOffer, setLoadingOffer] = useState<null | 'makeOffer' | 'reject' | 'accept' | 'cancel' | 'clearChat'>(null);
  
  if (!offerData || !chatData || loading) {
  return (
    <OfferSkeleton />
    )
  }

  const currency = chatData.currencyType === CURRENCY.USD ? 'USD' : 'UZS';

  const handleChangePrice = () => {
    setIsEditing(true);
    baseInputRef.current!.focus();
    baseInputRef.current!.value = `${offerData.price}` === '0' ? '' : `${offerData.price}`;
  }

  const handleCancelEditingPrice = () => {
    setIsEditing(false);
    baseInputRef.current!.value = (offerData.price + '') || '';
  }

  const handleMakeOffer = () => {
    setLoadingOffer('makeOffer');
    if(!offerData.price){
      makeOffer(chatData.productId, { id: null, currencyType: currency, price: +baseInputRef.current!.value, quantity: 1 })
      .then(res => {
        offerData.price = res.data.price;
        reFetchMessages();
        offerData.status = OfferStatusEnum.PENDING;
        setIsEditing(false);
      }).catch(err => {
        logger.log(err);
      }).finally(() => {
        setLoadingOffer(null);
      })
    } else {
      makeOffer(chatData.productId, { id: null, currencyType: currency, price: +baseInputRef.current!.value, quantity: 1 })
      .then(res => {
        offerData.price = res.data.price;
        offerData.status = OfferStatusEnum.PENDING;
        reFetchMessages();
        setIsEditing(false);
      }).catch(err => {
        logger.log(err);
      }).finally(() => {
        setLoadingOffer(null);
      })
    }
  }

  const handleAcceptOffer = () => {
    setLoadingOffer('accept');
    changeOfferStatus({id: offerData.id, status: OfferStatusEnum.ACCEPTED})
      .then(res => {
        reFetchMessages();
        offerData.status = OfferStatusEnum.ACCEPTED 
      }).catch(err => {
        logger.log(err);
      }).finally(() => {
        setLoadingOffer(null);
      })
  }

  const handleRejectedOffer = () => {
    setLoadingOffer('reject');
    changeOfferStatus({id: offerData.id, status: OfferStatusEnum.REJECTED})
      .then(res => {
        reFetchMessages();
        offerData.status = OfferStatusEnum.REJECTED 
      }).catch(err => {
        logger.log(err);
      }).finally(() => {
        setLoadingOffer(null);
      })
  }

  const handleCancelBtn = () => {
    setLoadingOffer('cancel');
    changeOfferStatus({id: offerData.id, status: OfferStatusEnum.CANCELED})
      .then(res => {
        reFetchMessages();
        offerData.status = OfferStatusEnum.CANCELED
      }).catch(err => {
        logger.log(err);
      }).finally(() => {
        setLoadingOffer(null);
      })
  }

  const handleClearChatBtn = () => {
    setLoadingOffer('clearChat')
    ChatApi.clearChat(chatData.id).then(() => {
      
    })
  }


  const isLastUpdatedByBuyer = `${offerData.updatedBy}` !== `${userData.id}`;
  const isSeller = chatData.isSeller;
  const isBuyer = !isSeller; // offerData.offeredBy == userData.id;
  const offerCurrency = offerData.currencyType || currency;

  // NOTE: Buyer actions
  const callToActionBtn = (
    <BaseButton key='callToActionBtn' btnType='primary' className='make-offer__btn' onClick={handleChangePrice}>
      {t('enter_offer')}
    </BaseButton>
  )
  const changeBtn = (
    <BaseButton key='changeBtn' btnType='light-primary' onClick={handleChangePrice} className='make-offer__btn'>
      {t('change')}
    </BaseButton>
  )
  const makeOfferBtn = (
    <BaseButton key='makeOfferBtn' btnType='primary' onClick={handleMakeOffer} className='make-offer__btn' loading={loadingOffer === 'makeOffer'}>
      {t('make_offer')}
    </BaseButton>
  )
  const makeAnotherOfferBtn = (
    <BaseButton key='makeAnotherOfferBtn' btnType='primary' onClick={handleChangePrice} className='make-offer__btn'>
      {t('make_another_offer')}
    </BaseButton>
  )
  const cancelEditingPriceBtn = (
    <BaseButton key='cancelEditingPriceBtn' btnType='primary-outlined' onClick={handleCancelEditingPrice} className='make-offer__btn'>
      {t('cancel')}
    </BaseButton>
  )


  // NOTE: Seller actions
  const acceptBtn = (
    <BaseButton key='acceptBtn' btnType='primary' className='make-offer__btn' onClick={handleAcceptOffer}  loading={loadingOffer === 'accept'}>
      {t('accept')}
    </BaseButton>
  )
  const rejectBtn = (
    <BaseButton key='rejectBtn' btnType='light-primary' className='make-offer__btn' onClick={handleRejectedOffer} loading={loadingOffer === 'reject'}>
      {t('reject')}
    </BaseButton>
  )

  // NOTE: Shared actions
  const callToSellerBtn = offerData.sellerPhone && (
    <BaseButton key='callToSellerBtn' href={`tel:${offerData.sellerPhone}`} btnType='primary' className='make-offer__btn'>
      {t('call')}
    </BaseButton>
  )

  const callToBuyerBtn = offerData.buyerPhone && (
    <BaseButton key='callToBuyerBtn' href={`tel:${offerData.buyerPhone}`} btnType='primary' className='make-offer__btn'>
      {t('call')}
    </BaseButton>
  )

  const cancelBtn = (
    <BaseButton key='cancelBtn' btnType='light-primary' className='make-offer__btn' onClick={handleCancelBtn} loading={loadingOffer === 'cancel'}>
      {t('cancel')}
    </BaseButton>
  )

  // TODO arxivlangan chatlarda chiqadi
  const clearChatBtn = (
    <BaseButton key='clearChatBtn' btnType='light-primary' className='make-offer__btn' href='/'>
      {t('clear_chat')}
    </BaseButton>
  )
  const goHomeBtn = (
    <BaseButton key='goHomeBtn' btnType='primary' className='make-offer__btn' href='/'>
      {t('home')}
    </BaseButton>
  )

  let title: React.ReactNode = null;
  let subtitle = '';
  let price: string | number = '';
  let actions: React.ReactNode[] = [];
  switch (offerData.status) {
    // ================================================ PENDING ================================================
    case OfferStatusEnum.PENDING: { // BUYER can CHANGE or CALL && SELLER can ACCEPT or REJECT
      title = (
        <>
          <IconButton sx={{ backgroundColor: 'primary.main', color: '#fff', width: '36px', height: '36px' }} disableRipple>
            <CommonSvg name={svgNames.Offer} />
          </IconButton>
          <span>{t('offer')}</span>
        </>
      );
      const noOfferYet = !offerData.price;

      if (isBuyer) {
        subtitle = !isEditing ? t('your_offer') : t('offer_input_desc');
        price = noOfferYet ? 0 : offerData.price;
        actions = isEditing ? [cancelEditingPriceBtn, makeOfferBtn] : (noOfferYet ? [callToActionBtn] : [changeBtn, callToSellerBtn]);
      } else {
        // TODO offer berilmagan payt nima qilishni aniqlashtirish kerak
        subtitle = t('user_offer', { key: chatData.name });
        price = offerData.price;
        actions = [rejectBtn, acceptBtn];
      }
      break;
    }

    // ================================================ ACCEPTED ================================================
    case OfferStatusEnum.ACCEPTED: { // BUYER can CANCEL or CALL && SELLER can CANCEL or CALL
      title = (
        <>
          <IconButton sx={{ backgroundColor: 'success.main', color: '#fff', width: '36px', height: '36px' }} disableRipple>
            <FiCheck />
          </IconButton>
          <span>{t('accepted')}</span>
        </>
      );
      price = offerData.price;
      if (isBuyer) {
        subtitle = t('offer_accepted');
        actions = [cancelBtn, callToSellerBtn];
      } else {
        subtitle = t('seller_accepted_their_offer', { key: chatData.name });
        actions = [cancelBtn, callToBuyerBtn];
      }
      break;
    }

    // ================================================ CANCELED ================================================
    case OfferStatusEnum.CANCELED: { // BUYER can MAKE_ANOTHER_OFFER && SELLER can see ONLY_INFO
      title = (
        <>
          <IconButton sx={{ backgroundColor: 'error.main', color: '#fff', width: '36px', height: '36px' }} disableRipple>
            <CommonSvg name={svgNames.Close} />
          </IconButton>
          <span>{t('canceled')}</span>
        </>
      );
      subtitle = isLastUpdatedByBuyer ? t('seller_canceled') : t('you_canceled');
      price = offerData.price;
      if (isBuyer) {
        actions = isEditing ? [cancelEditingPriceBtn, makeOfferBtn] : [makeAnotherOfferBtn];
      } else {
        actions = [];
      }
      break;
    }

    // ================================================ REJECTED ================================================
    case OfferStatusEnum.REJECTED: { // BUYER can MAKE_ANOTHER_OFFER && SELLER can see ONLY_INFO
      title = (
        <>
          <IconButton sx={{ backgroundColor: 'error.main', color: '#fff', width: '36px', height: '36px' }} disableRipple>
            <CommonSvg name={svgNames.Close} />
          </IconButton>
          <span>{t('rejected')}</span>
        </>
      );
      price = offerData.price;
      if (isBuyer) {
        subtitle = t('offer_rejected');
        actions = isEditing ? [cancelEditingPriceBtn, makeOfferBtn] : [makeAnotherOfferBtn];
      } else {
        subtitle = t('you_rejected_offer');
        actions = [];
      }
      break;
    }

    // ================================================ SOLD ================================================
    case OfferStatusEnum.SOLD: { // BUYER can CLEAR CHAT or GO_HOME && SELLER can see ONLY_INFO
      title = (
        <>
          <IconButton sx={{ backgroundColor: 'gray.light', color: '#fff', width: '36px', height: '36px' }} disableRipple>
            <LiaFunnelDollarSolid />
          </IconButton>
          <span>{t('sold')}</span>
        </>
      );
      price = offerData.price;
      if (isBuyer) {
        subtitle = t('product_sold');
        actions = [clearChatBtn, goHomeBtn];
      } else {
        subtitle = t('you_sold_this');
        actions = [clearChatBtn, goHomeBtn];
      }
      break;
    }

    // NOTE: if status equals NOT_AVAILABLE then offer doesn't work kelishilyapti deb ko'rsatish kerak
    // TODO
    case OfferStatusEnum.NOT_AVAILABLE: {
      return null;
    }

    // NOTE: AVAILABLE status acts like PENDING status
    case OfferStatusEnum.AVAILABLE: // TODO
    default:
    // ================================================ PENDING ================================================
    case OfferStatusEnum.PENDING: { // BUYER can CHANGE or CALL && SELLER can ACCEPT or REJECT
      title = (
        <>
          <IconButton sx={{ backgroundColor: 'primary.main', color: '#fff', width: '36px', height: '36px' }} disableRipple>
            <CommonSvg name={svgNames.Offer} />
          </IconButton>
          <span>{t('offer')}</span>
        </>
      );
      const noOfferYet = !offerData.price;

      if (isBuyer) {
        subtitle = t('your_offer');
        price = noOfferYet ? 0 : offerData.price;
        actions = noOfferYet ? [callToActionBtn] : [changeBtn, callToSellerBtn];
      } else {
        // TODO offer berilmagan payt nima qilishni aniqlashtirish kerak
        subtitle = t('user_offer', { key: chatData.name });
        price = offerData.price;
        actions = [rejectBtn, acceptBtn];
      }
      break;
    }
  }
  if ('changedInputPrice') {
    // BUYER can CHANGE or MAKE_OFFER && SELLER can not change price
  }


  return (
    <StyledOffer className='make-offer'>
      <div className='make-offer__inner'>
        <header className='make-offer__header'>
          <h5 className='make-offer__header-title'>
            {title}
          </h5>
        </header>
        <div className='make-offer__main'>
          <h6 className='make-offer__main-title'>
            {subtitle}
          </h6>
          {
            !isEditing && (
              <p className='make-offer__main-price'>
                {numberService.insertSpace(price)} {t(offerCurrency)}
              </p>
            )
          }
          <p className='make-offer__main-price'>
            <label className={ isEditing ? '' : 'visuallyhidden' }>
              <BaseInput
                type="number"
                disabled={loadingOffer}
                ref={baseInputRef}
                placeholder='0'
                onKeyPress={(event : React.KeyboardEvent<HTMLInputElement>) => {
                  if (event?.key === '-' || event?.key === '+') {
                    event.preventDefault();
                  }
                  if(event?.target?.value.length===12){
                    event.preventDefault();
                  }

                }}
              />
              <span>{t(offerCurrency)}</span>
            </label>
          </p>
          <div className={'make-offer__actions'}>
            {actions}
          </div>
        </div>
      </div>
      <div className='make-offer__original-price'>
        {t('actual_price', {
          key: chatData.currencyType === CURRENCY.USD
            ? t('product_price_usd', { key: numberService.insertSpace(chatData.productPrice) })
            : t('product_price_uzs', { key: numberService.insertSpace(chatData.productPrice) })
        })}
      </div>
    </StyledOffer>
  )
}

export default Offer;
